import { FormikErrors, FormikTouched } from 'formik';
import styled from 'styled-components';
import {
  FormGroup,
  FormHelpText,
  FormInput,
  FormRadio,
  FormTextarea,
  spacer24,
} from '@spotify-internal/encore-web';
import { useT } from '@spotify-internal/i18n-core';
import { formBodyHeight } from './constants';
import { FORM_CASES, FormValues } from './types';

const Container = styled.div`
  min-height: ${formBodyHeight};
  display: flex;
  flex-direction: column;
  padding: ${spacer24} 0;
`;

const RadioContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const StyledTextarea = styled(FormTextarea)`
  min-height: 100px;
`;

interface RelocationFormProps {
  values: FormValues;
  handleChange: (e: React.ChangeEvent<any>) => void;
  handleBlur: (e: any) => void;
  errors: FormikErrors<FormValues>;
  touched: FormikTouched<FormValues>;
  disableEmailInput: boolean;
  firstBtnRef: React.MutableRefObject<HTMLElement | null>;
}

export const RelocationForm = ({
  values,
  handleChange,
  handleBlur,
  errors,
  touched,
  disableEmailInput,
  firstBtnRef,
}: RelocationFormProps) => {
  const t = useT();
  const caseType = values.case;

  const renderAlbumURIsFormGroup = (albumURIs: string) => {
    return (
      <FormGroup
        label={t(
          'atlas.relocation.labels.album_uris',
          'Album URI(s) or URL(s) of the affected product(s)',
          'Label for text input for album URI(s) or URL(s) of the affected product',
        )}
        labelFor="albumURIs"
      >
        <FormInput
          type="text"
          id="albumURIs"
          name="albumURIs"
          data-cy="albumURIs"
          value={albumURIs}
          onChange={handleChange}
        />
        {touched.albumURIs && errors.albumURIs && (
          <FormHelpText
            error
            id="contact-error-albumURIs"
            data-testid="contact-error-albumURIs"
          >
            {errors.albumURIs}
          </FormHelpText>
        )}
      </FormGroup>
    );
  };

  return (
    <Container>
      <RadioContainer>
        {touched.case && errors.case && (
          <FormHelpText error id="contact-error-case">
            {errors.case}
          </FormHelpText>
        )}
        <FormGroup inline>
          <FormRadio
            type="radio"
            data-cy={FORM_CASES.MY_RELEASE_ON_ANOTHER_PAGE}
            id={FORM_CASES.MY_RELEASE_ON_ANOTHER_PAGE}
            name="case"
            value={FORM_CASES.MY_RELEASE_ON_ANOTHER_PAGE}
            checked={caseType === FORM_CASES.MY_RELEASE_ON_ANOTHER_PAGE}
            onBlur={handleBlur}
            ref={firstBtnRef}
            onChange={handleChange}
          >
            {t(
              'atlas.relocation.radio_labels.my_release_on_another_page',
              'My release is on another page',
              'Label text for a radio input to select a type of content relocation form',
            )}
          </FormRadio>
          <FormRadio
            type="radio"
            data-cy={FORM_CASES.ANOTHER_ARTIST_ON_MY_PAGE}
            id={FORM_CASES.ANOTHER_ARTIST_ON_MY_PAGE}
            name="case"
            value={FORM_CASES.ANOTHER_ARTIST_ON_MY_PAGE}
            checked={caseType === FORM_CASES.ANOTHER_ARTIST_ON_MY_PAGE}
            onBlur={handleBlur}
            onChange={handleChange}
          >
            {t(
              'atlas.relocation.radio_labels.another_artist_on_my_page',
              `Another artist's release is on my page`,
              'Label text for a radio input to select a type of content relocation form',
            )}
          </FormRadio>
          <FormRadio
            type="radio"
            data-cy={FORM_CASES.MOVE_MY_RELEASE}
            id={FORM_CASES.MOVE_MY_RELEASE}
            name="case"
            value={FORM_CASES.MOVE_MY_RELEASE}
            checked={caseType === FORM_CASES.MOVE_MY_RELEASE}
            onBlur={handleBlur}
            onChange={handleChange}
          >
            {t(
              'atlas.relocation.radio_labels.move_my_release',
              'I need a release moved to a new artist page',
              'Label text for a radio input to select a type of content relocation form',
            )}
          </FormRadio>
        </FormGroup>
      </RadioContainer>
      <FormGroup
        label={t(
          'atlas.relocation.labels.business_email',
          'Business e-mail',
          `Label for text input for user's business e-mail`,
        )}
        labelFor="email"
      >
        <FormInput
          disabled={disableEmailInput}
          type="text"
          id="email"
          name="email"
          data-cy="email"
          value={values.email}
          onChange={handleChange}
        />
        {touched.email && errors.email && (
          <FormHelpText error id="contact-error-email">
            {errors.email}
          </FormHelpText>
        )}
      </FormGroup>
      {caseType === FORM_CASES.MY_RELEASE_ON_ANOTHER_PAGE && (
        <>
          <FormGroup
            label={t(
              'atlas.relocation.labels.destination_artist_uri',
              'Correct Artist URI or URL (where the release should be moved to)',
              'Label for text input for the correct Artist URI or URL',
            )}
            labelFor="destinationArtistURI"
          >
            <FormInput
              type="text"
              id="destinationArtistURI"
              name="destinationArtistURI"
              data-cy="destinationArtistURI"
              value={values.destinationArtistURI}
              onChange={handleChange}
            />
            {touched.destinationArtistURI && errors.destinationArtistURI && (
              <FormHelpText error id="contact-error-destinationArtistURI">
                {errors.destinationArtistURI}
              </FormHelpText>
            )}
          </FormGroup>
          {renderAlbumURIsFormGroup(values.albumURIs)}
          <FormGroup
            label={t(
              'atlas.relocation.labels.incorrect_artist_uri',
              'Incorrect Artist URI or URL',
              'Label for text input for the incorrect Artist URI or URL',
            )}
            labelFor="incorrectArtistURI"
          >
            <FormInput
              type="text"
              id="incorrectArtistURI"
              name="incorrectArtistURI"
              data-cy="incorrectArtistURI"
              value={values.incorrectArtistURI}
              onChange={handleChange}
            />
            {touched.incorrectArtistURI && errors.incorrectArtistURI && (
              <FormHelpText error id="contact-error-incorrectArtistURI">
                {errors.incorrectArtistURI}
              </FormHelpText>
            )}
          </FormGroup>
        </>
      )}
      {caseType === FORM_CASES.ANOTHER_ARTIST_ON_MY_PAGE && (
        <>
          <FormGroup
            label={t(
              'atlas.relocation.labels.my_artist_uri',
              'My Artist URI or URL',
              `Label for text input for the user's Artist URI or URL`,
            )}
            labelFor="myArtistURI"
          >
            <FormInput
              type="text"
              id="myArtistURI"
              name="myArtistURI"
              data-cy="myArtistURI"
              value={values.myArtistURI}
              onChange={handleChange}
            />
            {touched.myArtistURI && errors.myArtistURI && (
              <FormHelpText error id="contact-error-myArtistURI">
                {errors.myArtistURI}
              </FormHelpText>
            )}
          </FormGroup>
          {renderAlbumURIsFormGroup(values.albumURIs)}
        </>
      )}
      {caseType === FORM_CASES.MOVE_MY_RELEASE && (
        <>
          <FormGroup
            label={t(
              'atlas.relocation.labels.current_artist_uri',
              'Current Artist URI or URL',
              'Label for text input for the current Artist URI or URL',
            )}
            labelFor="currentArtistURI"
          >
            <FormInput
              type="text"
              id="currentArtistURI"
              name="currentArtistURI"
              data-cy="currentArtistURI"
              value={values.currentArtistURI}
              onChange={handleChange}
            />
            {touched.currentArtistURI && errors.currentArtistURI && (
              <FormHelpText error id="contact-error-currentArtistURI">
                {errors.currentArtistURI}
              </FormHelpText>
            )}
          </FormGroup>
          {renderAlbumURIsFormGroup(values.albumURIs)}
          <FormGroup
            label={t(
              'atlas.relocation.labels.artist_name',
              'Artist name',
              'Label for text input for the artist name',
            )}
            labelFor="artistName"
          >
            <FormInput
              type="text"
              id="artistName"
              name="artistName"
              data-cy="artistName"
              value={values.artistName}
              onChange={handleChange}
            />
            {touched.artistName && errors.artistName && (
              <FormHelpText error id="contact-error-artistName">
                {errors.artistName}
              </FormHelpText>
            )}
          </FormGroup>
        </>
      )}
      <FormGroup
        label={t(
          'atlas.relocation.labels.comments',
          'Optional comments or context to share',
          'Label for text input for optional comments from the user',
        )}
        labelFor="comments"
      >
        <StyledTextarea
          type="text"
          id="comments"
          name="comments"
          data-cy="comments"
          value={values.comments}
          onChange={handleChange}
        />
        {touched.comments && errors.comments && (
          <FormHelpText error id="contact-error-comments">
            {errors.comments}
          </FormHelpText>
        )}
      </FormGroup>
    </Container>
  );
};
